import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatError } from "../lib/api";
import { Clock, MapPin, Send } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Pilgrimage() {
  const [packages, setPackages] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", travelers: 2, preferred_month: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { api.get("/travel/packages").then((r) => setPackages(r.data.items)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/travel/enquiries", form);
      toast.success("Enquiry received — we'll reach out within 24 hours.");
      setForm({ name: "", email: "", phone: "", travelers: 2, preferred_month: "", message: "" });
    } catch (err) { toast.error(formatError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div data-testid="pilgrimage-page" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <div className="max-w-3xl">
        <div className="overline">॥ कम्बोडिया यात्रा ॥</div>
        <h1 className="font-serif text-5xl lg:text-6xl text-[#4A0E17] mt-2 leading-tight">Pancham Dham Pilgrimage</h1>
        <p className="text-[#63534E] mt-5 text-lg leading-relaxed">
          Travel the Khmer-Shaiva corridor — Pancham Dham, Angkor Wat, the serene faces of Bayon. Small-group yatras led by Vedic scholars with sattvik meals and daily aarti.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-7 mt-14">
        {packages.map((p) => (
          <div key={p.id} data-testid={`package-${p.slug}`} className="bg-[#FFFDF9] sacred-border rounded-2xl overflow-hidden transition-sacred gold-glow">
            <div className="h-56 overflow-hidden">
              <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover" />
            </div>
            <div className="p-6">
              <div className="flex items-center gap-4 text-xs text-[#8C6D3B]">
                <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {p.duration_days} days</span>
                <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> Siem Reap</span>
              </div>
              <h3 className="font-serif text-xl text-[#4A0E17] mt-3 leading-tight">{p.title}</h3>
              <p className="text-sm text-[#63534E] mt-3 line-clamp-3">{p.summary}</p>
              <div className="mt-6 flex items-center justify-between">
                <div>
                  <div className="font-mono text-[#E65100] text-xl font-semibold">${p.price_usd.toFixed(0)}</div>
                  <div className="text-[0.7rem] text-[#63534E]">per traveler</div>
                </div>
                <Link data-testid={`view-package-${p.slug}`} to={`/pilgrimage/${p.slug}`} className="btn-primary text-sm">View & Book</Link>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Enquiry */}
      <section id="enquiry" className="mt-28 grid lg:grid-cols-2 gap-14 items-start">
        <div>
          <div className="overline">Custom Yatra</div>
          <h2 className="font-serif text-4xl text-[#4A0E17] mt-2">Need a different itinerary?</h2>
          <p className="text-[#63534E] mt-4 leading-relaxed">
            Family satsang groups, corporate pilgrimage retreats, dedicated puja requests — tell us what you need and our yatra desk will tailor a plan.
          </p>
          <ul className="mt-6 space-y-3 text-[#1F1412]">
            <li>• Private family or community groups (6–40 pax)</li>
            <li>• Add Varanasi / Rameshwaram pre-leg from India</li>
            <li>• Elderly-friendly slow itineraries available</li>
            <li>• Diaspora-friendly departure: BKK, SIN, DEL, BLR</li>
          </ul>
        </div>
        <form data-testid="enquiry-form" onSubmit={submit} className="bg-[#FFFDF9] sacred-border rounded-2xl p-7 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <Input data-testid="enquiry-name" required placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
            <Input data-testid="enquiry-email" required type="email" placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            <Input data-testid="enquiry-phone" placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
            <Input data-testid="enquiry-travelers" type="number" min="1" max="50" placeholder="Travelers" value={form.travelers} onChange={(e) => setForm({ ...form, travelers: parseInt(e.target.value) || 1 })} className="bg-white border-[#D4AF37]/40 h-11" />
            <Input data-testid="enquiry-month" placeholder="Preferred month" value={form.preferred_month} onChange={(e) => setForm({ ...form, preferred_month: e.target.value })} className="bg-white border-[#D4AF37]/40 h-11" />
          </div>
          <Textarea data-testid="enquiry-message" placeholder="Share your intent and special requests…" rows={4} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="bg-white border-[#D4AF37]/40" />
          <Button data-testid="enquiry-submit" type="submit" disabled={submitting} className="w-full bg-[#4A0E17] hover:bg-[#36080F] rounded-full h-12 text-base">
            {submitting ? "Sending…" : <><Send className="w-4 h-4 mr-2" /> Request Yatra Plan</>}
          </Button>
        </form>
      </section>
    </div>
  );
}
